import { useEffect, useState } from "react";
import { api, formatApiError, formatDate } from "@/lib/api";
import { toast } from "sonner";
import { X, RotateCcw, CalendarClock, Wallet } from "lucide-react";

// Modal used to change a bill's due date. Behavior branches by scope:
//   - "instance": (default) move ONLY this one occurrence. Snapshots
//     `original_due_date` so we can restore it later; the recurring cadence
//     is untouched.
//   - "future": (only offered for recurring bills) permanently shift THIS
//     instance and every LATER sibling in the chain to the new day-of-month.
//     Clears the reschedule snapshot on all affected rows so the materializer
//     uses the new day going forward.
//
// Props:
//   bill          — the bill row being rescheduled
//   onClose()     — closes the modal
//   onUpdated(b)  — merge a single updated bill row into parent state
//   onBulkUpdated(bills[])  — optional; merge many rows into parent state
//                             when scope=future returns multiple docs. Falls
//                             back to onUpdated per-row if not provided.
export default function RescheduleBillModal({ bill, onClose, onUpdated, onBulkUpdated }) {
  const [newDate, setNewDate] = useState(bill?.due_date || "");
  const [scope, setScope] = useState("instance");
  const [saving, setSaving] = useState(false);
  // Paycheck shortcut buttons. We surface up to two:
  //   - "This paycheck":  the LATEST paycheck whose date is <= today. Represents
  //     the money currently funding this pay period, whether already received
  //     or expected today. Only shown when a plausible current paycheck exists
  //     (within the last ~16 days) and the new date isn't already on it.
  //   - "Next paycheck":  the EARLIEST paycheck whose date is > today.
  //     Represents the paycheck that starts the FOLLOWING pay period.
  // Showing both prevents the trap where the current paycheck being marked
  // "received" silently pushes the shortcut to the following period.
  const [currentPaycheck, setCurrentPaycheck] = useState(null);
  const [nextPaycheck, setNextPaycheck] = useState(null);

  useEffect(() => {
    setNewDate(bill?.due_date || "");
    setScope("instance");
    if (!bill) return;
    let cancelled = false;
    const todayIso = new Date().toISOString().slice(0, 10);
    // Pull from the forecast pay-periods endpoint instead of raw /incomes so
    // we get PROJECTED recurring paychecks (biweekly, monthly, etc.) — not
    // just the stored base rows. Otherwise users whose next paycheck is a
    // projected occurrence (very common for biweekly earners) would never
    // see the "Move to next paycheck" shortcut.
    api.get("/forecast/pay-periods")
      .then(({ data }) => {
        if (cancelled) return;
        // Each pay period has a canonical `pay_date` (start of the period).
        // Sort ascending; dedupe by date.
        const seen = new Set();
        const dates = [];
        (data || []).forEach((pp) => {
          const d = pp?.pay_date;
          if (d && !seen.has(d)) { seen.add(d); dates.push(d); }
        });
        dates.sort();
        const sixteenDaysAgo = new Date(Date.now() - 16 * 86400 * 1000).toISOString().slice(0, 10);
        // Current paycheck: latest pay_date ≤ today, within 16-day lookback.
        const currentIso = dates.filter((d) => d <= todayIso && d >= sixteenDaysAgo).slice(-1)[0] || null;
        // Next paycheck: earliest pay_date strictly after today.
        const nextIso = dates.find((d) => d > todayIso) || null;
        // Human-friendly source label for each: pull from the matching period.
        const findSource = (iso) => {
          const pp = (data || []).find((p) => p.pay_date === iso);
          return pp ? (pp.source || pp.label || "") : "";
        };
        const billIso = bill?.due_date || todayIso;
        const daysBetween = (a, b) => Math.abs(
          Math.round((new Date(a + "T00:00:00").getTime() - new Date(b + "T00:00:00").getTime()) / 86400000)
        );
        setCurrentPaycheck(
          currentIso && daysBetween(currentIso, billIso) <= 45
            ? { expected_date: currentIso, source: findSource(currentIso) }
            : null
        );
        setNextPaycheck(
          nextIso && daysBetween(nextIso, billIso) <= 45
            ? { expected_date: nextIso, source: findSource(nextIso) }
            : null
        );
      })
      .catch(() => { if (!cancelled) { setCurrentPaycheck(null); setNextPaycheck(null); } });
    return () => { cancelled = true; };
  }, [bill?.id, bill?.due_date]);
  if (!bill) return null;

  const isRescheduled = !!bill.original_due_date;
  const isRecurring = !!bill.is_recurring;
  const unchanged = newDate === bill.due_date;

  const applyMany = (rows, deletedIds) => {
    if (!rows || rows.length === 0) return;
    if (typeof onBulkUpdated === "function") { onBulkUpdated(rows, deletedIds || []); return; }
    // Fallback: notify parent one-by-one via onUpdated.
    rows.forEach((r) => onUpdated?.(r));
  };

  const save = async () => {
    if (!newDate || (scope === "instance" && newDate === bill.due_date)) { onClose(); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/bills/${bill.id}/reschedule`, {
        new_due_date: newDate,
        scope,
      });
      if (Array.isArray(data?.updated_bills) && data.updated_bills.length > 0) {
        applyMany(data.updated_bills, data?.deleted_bill_ids);
      } else {
        onUpdated?.(data);
      }
      const suffix = scope === "future" ? " and all future occurrences" : "";
      const label = `Moved “${bill.name}”${suffix} to day ${new Date(newDate + "T00:00:00").getDate()}`;
      // If the bill has an online pay portal, chain the reschedule into an
      // action prompt to open it (nudges the user from intent → action).
      if (bill.pay_url && scope === "instance") {
        toast.success(label, {
          duration: 8000,
          action: {
            label: "Open pay portal",
            onClick: () => window.open(bill.pay_url, "_blank", "noopener,noreferrer"),
          },
        });
      } else {
        toast.success(label);
      }
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const reset = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/bills/${bill.id}/reset-due-date`);
      onUpdated?.(data);
      toast.success(`Restored “${bill.name}” to ${formatDate(data.due_date)}`);
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      data-testid="reschedule-modal-backdrop"
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white shadow-2xl p-6"
        onClick={(e) => e.stopPropagation()}
        data-testid="reschedule-modal"
      >
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="flex items-center gap-2 text-[#4A6148]">
              <CalendarClock size={18} />
              <div className="text-xs font-semibold uppercase tracking-widest">Reschedule bill</div>
            </div>
            <h2 className="font-heading text-xl text-[#2C302E] mt-1">{bill.name}</h2>
            <p className="text-xs text-[#59605C] mt-1 leading-relaxed">
              {isRecurring
                ? "Choose whether this new date applies to only this one occurrence, or to this and every future occurrence in the recurring series."
                : "Move this bill’s due date to any future day — useful when you don’t have enough cash this pay period."}
            </p>
          </div>
          <button
            className="text-[#8A918D] hover:text-[#2C302E] shrink-0"
            onClick={onClose}
            aria-label="Close"
            data-testid="reschedule-close"
          >
            <X size={20} />
          </button>
        </div>

        <div className="space-y-3">
          <div className="text-xs text-[#59605C]">
            Currently due <span className="font-semibold text-[#2C302E]">{formatDate(bill.due_date)}</span>
            {isRescheduled && (
              <span className="ml-1 italic">
                (rescheduled from {formatDate(bill.original_due_date)})
              </span>
            )}
          </div>
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-widest text-[#59605C]">New due date</span>
            <input
              type="date"
              value={newDate}
              onChange={(e) => setNewDate(e.target.value)}
              className="mt-1 w-full px-3 py-2 rounded-xl border border-[#CDC7BB] bg-white text-[#2C302E] focus:outline-none focus:border-[#4A6148]"
              data-testid="reschedule-date-input"
            />
          </label>

          {/* Quick shortcuts: line up the new due date with either the
              paycheck currently funding this period ("this paycheck") or the
              following one ("next paycheck"). Hidden individually when the
              date is already on that paycheck or when no such paycheck exists. */}
          {currentPaycheck && currentPaycheck.expected_date !== newDate && (
            <button
              type="button"
              onClick={() => setNewDate(currentPaycheck.expected_date)}
              className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-xl border border-[#B8CFB1] bg-[#EEF4EC] text-[#2F4E32] text-xs hover:bg-[#DDE9D8] transition-colors"
              data-testid="reschedule-this-paycheck"
            >
              <span className="flex items-center gap-2">
                <Wallet size={14} />
                <span className="font-semibold">Move to this paycheck</span>
              </span>
              <span className="text-[#4A6148] font-mono">
                {formatDate(currentPaycheck.expected_date)}
              </span>
            </button>
          )}
          {nextPaycheck && nextPaycheck.expected_date !== newDate && (
            <button
              type="button"
              onClick={() => setNewDate(nextPaycheck.expected_date)}
              className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-xl border border-[#B8CFB1] bg-[#EEF4EC] text-[#2F4E32] text-xs hover:bg-[#DDE9D8] transition-colors"
              data-testid="reschedule-next-paycheck"
            >
              <span className="flex items-center gap-2">
                <Wallet size={14} />
                <span className="font-semibold">Move to next paycheck</span>
              </span>
              <span className="text-[#4A6148] font-mono">
                {formatDate(nextPaycheck.expected_date)}
              </span>
            </button>
          )}

          {isRecurring && (
            <fieldset className="mt-2 rounded-xl border border-[#E5E0D5] p-3 bg-[#FBFAF6]" data-testid="reschedule-scope-group">
              <legend className="px-1 text-[10px] font-semibold uppercase tracking-widest text-[#59605C]">
                Apply this change to
              </legend>
              <label className="flex items-start gap-2 py-1.5 cursor-pointer">
                <input
                  type="radio"
                  name="reschedule-scope"
                  value="instance"
                  checked={scope === "instance"}
                  onChange={() => setScope("instance")}
                  className="mt-1 accent-[#4A6148]"
                  data-testid="reschedule-scope-instance"
                />
                <span className="text-xs text-[#2C302E] leading-snug">
                  <span className="font-semibold">This occurrence only</span>
                  <span className="block text-[#59605C]">
                    Just move this one bill. Future months keep their original day.
                  </span>
                </span>
              </label>
              <label className="flex items-start gap-2 py-1.5 cursor-pointer">
                <input
                  type="radio"
                  name="reschedule-scope"
                  value="future"
                  checked={scope === "future"}
                  onChange={() => setScope("future")}
                  className="mt-1 accent-[#4A6148]"
                  data-testid="reschedule-scope-future"
                />
                <span className="text-xs text-[#2C302E] leading-snug">
                  <span className="font-semibold">This and all future occurrences</span>
                  <span className="block text-[#59605C]">
                    Shift the recurring day for this and every later month. Past instances are unchanged.
                  </span>
                </span>
              </label>
            </fieldset>
          )}
        </div>

        <div className="mt-6 flex items-center justify-between gap-2 flex-wrap">
          {isRescheduled && scope === "instance" ? (
            <button
              className="btn-ghost !px-3 !py-2 text-xs text-[#7A2E24] hover:bg-[#F5D9D1]"
              onClick={reset}
              disabled={saving}
              data-testid="reschedule-reset"
            >
              <RotateCcw size={14} /> Reset to original ({formatDate(bill.original_due_date)})
            </button>
          ) : <span />}
          <div className="flex gap-2">
            <button
              className="btn-ghost !px-3 !py-2 text-sm"
              onClick={onClose}
              disabled={saving}
              data-testid="reschedule-cancel"
            >
              Cancel
            </button>
            <button
              className="btn-primary text-sm"
              onClick={save}
              disabled={saving || (scope === "instance" && unchanged) || !newDate}
              data-testid="reschedule-save"
            >
              {saving ? "Saving…" : (scope === "future" ? "Save for all future" : "Save new date")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
